/**
 * Store Zustand pour la gestion des devis
 * Remplace useQuotes, useCreateQuote, useUpdateQuote, useDeleteQuote
 * Architecture 3-couches conforme au CONTEXT.md
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { quotesAPI } from '../utils/api';
import type { Quote } from '../types/models';

interface QuotesState {
  // État des données
  quotes: Quote[];
  currentQuote: Quote | null;
  
  // État de chargement
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  
  // État d'erreur
  error: string | null;
  
  // Actions de lecture
  fetchQuotes: (params?: {
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  }) => Promise<void>;
  
  fetchQuote: (id: string) => Promise<void>;
  
  // Actions d'écriture
  createQuote: (quoteData: Partial<Quote>) => Promise<Quote>;
  updateQuote: (id: string, quoteData: Partial<Quote>) => Promise<Quote>;
  deleteQuote: (id: string) => Promise<void>;
  
  // Actions spéciales
  duplicateQuote: (id: string) => Promise<Quote>;
  
  // Actions utilitaires
  clearError: () => void;
  clearCurrentQuote: () => void;
}

export const useQuotesStore = create<QuotesState>()(
  devtools(
    (set, get) => ({
      // État initial
      quotes: [],
      currentQuote: null,
      isLoading: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      error: null,

      // Actions de lecture
      fetchQuotes: async (_params) => {
        set({ isLoading: true, error: null });
        try {
          // Note: quotesAPI.getAll returns Quote[] directly
          const data = await quotesAPI.getAll();
          const quotes = Array.isArray(data) ? data : ((data as any)?.quotes || data || []);
          set({
            quotes,
            isLoading: false
          });
        } catch (error: any) {
          set({
            error: error.message || 'Erreur lors de la récupération des devis',
            isLoading: false
          });
        }
      },

      fetchQuote: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const quote = await quotesAPI.getById(id);
          set({ 
            currentQuote: quote, 
            isLoading: false 
          });
        } catch (error: any) {
          set({ 
            error: error.message || 'Erreur lors de la récupération du devis',
            isLoading: false 
          });
        }
      },

      // Actions d'écriture
      createQuote: async (quoteData) => {
        set({ isCreating: true, error: null });
        try {
          const newQuote = await quotesAPI.create(quoteData);
          set(state => ({
            quotes: [newQuote, ...state.quotes],
            isCreating: false
          }));
          return newQuote;
        } catch (error: any) {
          set({ 
            error: error.message || 'Erreur lors de la création du devis',
            isCreating: false 
          });
          throw error;
        }
      },

      updateQuote: async (id, quoteData) => {
        set({ isUpdating: true, error: null });
        try {
          const updatedQuote = await quotesAPI.update(id, quoteData);
          set(state => ({
            quotes: state.quotes.map(q => q.id === id ? updatedQuote : q),
            currentQuote: state.currentQuote?.id === id ? updatedQuote : state.currentQuote,
            isUpdating: false
          }));
          return updatedQuote;
        } catch (error: any) {
          set({ 
            error: error.message || 'Erreur lors de la mise à jour du devis',
            isUpdating: false 
          });
          throw error;
        }
      },

      deleteQuote: async (id) => {
        set({ isDeleting: true, error: null });
        try {
          await quotesAPI.delete(id);
          set(state => ({
            quotes: state.quotes.filter(q => q.id !== id),
            currentQuote: state.currentQuote?.id === id ? null : state.currentQuote,
            isDeleting: false
          }));
        } catch (error: any) {
          set({ 
            error: error.message || 'Erreur lors de la suppression du devis',
            isDeleting: false 
          });
          throw error;
        }
      },

      // Actions spéciales
      duplicateQuote: async (id) => {
        set({ isCreating: true, error: null });
        try {
          const duplicatedQuote = await quotesAPI.duplicate(id);
          set(state => ({
            quotes: [duplicatedQuote, ...state.quotes],
            isCreating: false
          }));
          return duplicatedQuote;
        } catch (error: any) {
          set({ 
            error: error.message || 'Erreur lors de la duplication du devis',
            isCreating: false 
          });
          throw error;
        }
      },

      // Actions utilitaires
      clearError: () => set({ error: null }),
      clearCurrentQuote: () => set({ currentQuote: null }),
    }),
    {
      name: 'quotes-store',
    }
  )
);
